import { supabase } from './supabase';

export const auditLogService = {
  /**
   * Get audit log entries with optional filtering
   */
  async getLogs({
    userId,
    action,
    resourceType,
    resourceId,
    startDate,
    endDate,
    limit = 50,
    offset = 0
  } = {}) {
    let query = supabase
      .from('audit_log')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (userId) query = query.eq('user_id', userId);
    if (action) query = query.eq('action', action);
    if (resourceType) query = query.eq('resource_type', resourceType);
    if (resourceId) query = query.eq('resource_id', resourceId);
    if (startDate) query = query.gte('created_at', startDate);
    if (endDate) query = query.lte('created_at', endDate);

    const { data, error, count } = await query;
    if (error) throw error;
    return { data, count };
  },

  /**
   * Get logs for a specific resource
   */
  async getLogsByResource(resourceType, resourceId) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .eq('resource_type', resourceType)
      .eq('resource_id', resourceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  /**
   * Get distinct action types for filtering
   */
  async getActionTypes() {
    const { data, error } = await supabase
      .from('audit_log')
      .select('action')
      .limit(1000);

    if (error) throw error;
    return [...new Set(data.map(d => d.action))].sort();
  },

  /**
   * Get distinct resource types for filtering
   */
  async getResourceTypes() {
    const { data, error } = await supabase
      .from('audit_log')
      .select('resource_type')
      .limit(1000);

    if (error) throw error;
    return [...new Set(data.map(d => d.resource_type))].sort();
  },

  /**
   * Log an audit event from the client side.
   * Wraps the RPC function for convenience.
   */
  async logAction(action, resourceType, resourceId = null, oldValue = null, newValue = null, metadata = {}) {
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_action: action,
      p_resource_type: resourceType,
      p_resource_id: resourceId,
      p_old_value: oldValue,
      p_new_value: newValue,
      p_metadata: metadata
    });

    if (error) {
      console.error('Audit log error:', error);
      // Don't throw - audit logging shouldn't break the main operation
    }

    return data;
  },

  /**
   * Get recent activity for a user
   */
  async getUserActivity(userId, limit = 20) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  /**
   * Get activity summary (counts by action type) for a time period
   */
  async getActivitySummary(startDate, endDate) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('action')
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (error) throw error;

    const summary = {};
    data.forEach(log => {
      summary[log.action] = (summary[log.action] || 0) + 1;
    });

    return summary;
  },

  /**
   * Export logs to CSV format
   */
  async exportLogs(filters = {}) {
    const { data, error } = await this.getLogs({ ...filters, limit: 10000 });

    if (error) throw error;

    const headers = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Details'];
    const rows = data.map(log => [
      new Date(log.created_at).toISOString(),
      log.user_email || 'System',
      log.action,
      log.resource_type,
      log.resource_id || '',
      JSON.stringify(log.new_value || log.metadata || {})
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csv;
  }
};
